import ClubActionButtons from '@/components/clubs/ClubActionButtons';
import ClubAmenities from "@/components/clubs/ClubAmenities";
import FavoriteButton from "@/components/FavoriteButton";
import GameSetup from "@/components/game-setup/GameSetup";
import { GameSetup as GameSetupType } from "@/components/game-setup/types";
import { IMAGES_BASE_URL } from "@/lib/config";
import { capitalizeCityName } from "@/lib/utils";
import { api } from "@/utils/api";
import { saveVisitedClub } from "@/utils/recentClubs";
import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";
import { IonBackButton, IonButton, IonButtons, IonContent, IonIcon, IonItem, IonList, IonPage, IonSpinner, IonToolbar } from "@ionic/react";
import { t } from 'i18next';
import { arrowBackOutline, shareOutline } from "ionicons/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

const Club: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const [course, setCourse] = useState<any>(null);
  const [showGameSetup, setShowGameSetup] = useState(false);
  const { t } = useTranslation();

  // Handle favorite status change
  const handleFavoriteChange = (isFavorite: boolean) => {
    setCourse((prevCourse: any) => ({
      ...prevCourse,
      isFavorite
    }));
  };

  document.title = course?.name;

  useEffect(() => {
    if (!courseId) return;

    const fetchCourse = async () => {
      try {
        const response = await api.get(`/golf-clubs/${courseId}`);
        const clubData = response.data;
        setCourse(clubData);

        // Save this club to recently visited clubs
        await saveVisitedClub(clubData);
      } catch (error) {
        console.error("Error fetching course:", error);
      }
    };

    // Note: Course photos functionality will be implemented in a future update

    fetchCourse();
    // fetchCoursePhotos();
  }, [courseId]);

  // No need for favorite status check here as it's handled by the FavoriteButton component

  if (!course) {
    return (
      <div className="flex justify-center items-center h-screen text-lg text-gray-500">
        <IonSpinner />
      </div>
    );
  }


  const handleSystemShare = async () => {
    const profileUrl = `${window.location.origin}/u/${courseId}`;
    const shareText = t('share_club_text', { courseId });

    // Check if we're on a native platform with Share plugin available
    if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Share')) {
      await Share.share({
        title: 'toto',
        text: shareText,
        url: profileUrl,
        dialogTitle: 'salut mon pote',
      });
    };
  };

  return (
    <IonPage>
      <IonContent fullscreen={true}>
        <IonToolbar color='transparent' className='px-3 md:hidden absolute top-safe left-0 right-0 z-10'>
          <IonButtons slot="start">
            <IonBackButton icon={arrowBackOutline} text="" className='text-black bg-white/60 backdrop-blur-sm rounded-full size-10 pl-1 text-xs' defaultHref="/dashboard" />
          </IonButtons>
          <IonButtons slot="end" className="gap-1">
            <IonButton onClick={async () => {
              await handleSystemShare();
            }} fill="clear" color="transparent" className="bg-white/60 backdrop-blur-sm rounded-full size-10">
              <IonIcon icon={shareOutline} className="size-5" />
            </IonButton>
            <FavoriteButton
              clubId={course.id}
              initialValue={course.isFavorite}
              color="transparent"
              fill="clear"
              themeAware={false}
              className="bg-white/60 backdrop-blur-sm rounded-full size-10"
              onFavoriteChange={handleFavoriteChange}
            />
          </IonButtons>
        </IonToolbar>
        {/* Hero Section */}
        <div className="relative h-96 mx-auto max-w-4xl bg-gray-900">
          <img src="/assets/images/bg/bg1.jpg" className="w-full h-full object-cover opacity-90" />
          <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black to-transparent"></div>

          {/* Logo and Title */}
          <div className="absolute bottom-4 left-0 right-0 flex items-center justify-between p-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg">
                <img src={`${IMAGES_BASE_URL}/${course.id}.jpg`} alt="Logo" className="size-full rounded-lg" />
              </div>
              <div className="z-20 flex flex-col text-white justify-end">
                <h1 className="text- md:text-2xl font-bold">{course.name}</h1>
                <p className="text-sm">
                  {capitalizeCityName(course.city)}, {t('Countries:' + capitalizeCityName(course.country))}
                </p>
              </div>
            </div>

            {/* Favorite Button */}
            {course && (
              <FavoriteButton
                clubId={course.id}
                initialValue={course.isFavorite}
                fill="clear"
                color="transparent"
                themeAware={false}
                className="z-20 hidden md:block bg-white/60 backdrop-blur-xl rounded font-semibold text-black"
                showText={true}
                onFavoriteChange={handleFavoriteChange}
              />
            )}
          </div>
        </div>
        <div className="p-2 max-w-4xl mx-auto -translate-y-4 rounded-xl bg-card">

          <ClubActionButtons
            course={course}
            onPlayClick={() => setShowGameSetup(true)}
          />
          {/* Main Content */}
          <section className="stats">
            {course.holes && (
              <div className="stat">
                <p className="text-sm text-gray-500">{t('holes')}</p>
                <p className="font-semibold">{course.holes}</p>
              </div>
            )}
            {course.architect && (
              <div className="stat">
                <p className="text-sm text-gray-500">{t('architect')}</p>
                <p className="font-semibold">{course.architect}</p>
              </div>
            )}
          </section>

          {/* Description */}
          <section className="my-8 px-2">
            <p className="text-sm leading-relaxed">
              {course.description || "Aucune description disponible pour ce club."}
            </p>
          </section>
          <hr />
          {/* Features & Amenities */}
          <section className="my-8">
            <h2 className="font-semibold ml-2 mb-2 text-xl">{t('amenities')}</h2>
            <ClubAmenities
              drivingRange={course.drivingRange}
              puttingGreen={course.puttingGreen}
              chippingGreen={course.chippingGreen}
              practiceBunker={course.practiceBunker}
              motorCart={course.motorCart}
              pullCart={course.pullCart}
              golfClubsRental={course.golfClubsRental}
              clubFitting={course.clubFitting}
              proShop={course.proShop}
              golfLessons={course.golfLessons}
              caddieHire={course.caddRingaieHire}
              restaurant={course.restaurant}
              receptionHall={course.receptionHall}
              changingRoom={course.changingRoom}
              lockers={course.lockers}
              lodgingOnSite={course.lodgingOnSite}
            />
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="font-semibold mb-4 ml-2 text-xl">{t('details')}</h2>
            <IonList className="space-y-4 bg-card rounded-lg -ml-2">
              {course.longitude && course.latitude && (
                <img className="rounded-lg ml-2" src={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${course.longitude},${course.latitude},${13}/400x400?access_token=pk.eyJ1IjoiZXhhbXBsZXMiLCJhIjoiY2p1dHRybDR5MGJuZjQzcGhrZ2doeGgwNyJ9.a-vxW4UaxOoUMWUTGnEArw`} />
              )}
              {course.address && (
                <IonItem onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${course.name}, ${course.address}, ${course.city}`, '_blank')}>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">
                      {t('address')}
                    </span>
                    <p className="text-sm flex flex-col">
                      <span className="mr-2">{course.address}</span>
                      <span className="mr-2">{course.zip} {capitalizeCityName(course.city)}</span>
                      <span>{course.country}</span>
                    </p>
                  </div>
                </IonItem>
              )}
              {course.phone && (
                <IonItem>
                  <div className="flex text-sm flex-col">
                    <span className="mr-2 text-sm text-gray-500">
                      {t('phone')}
                    </span>
                    <a className="text-blue-600 hover:underline" href={`tel:${course.phone}`}>{course.phone}</a>
                  </div>
                </IonItem>
              )}
              {course.websiteUrl && (
                <IonItem >
                  <div className="flex text-sm flex-col text-gray-700">
                    <span className="mr-2 text-gray-500">
                      {t('website')}
                    </span>
                    <a
                      href={course.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {course.websiteUrl}
                    </a>
                  </div>
                </IonItem>
              )}
            </IonList>

          </section>
        </div>
        {/* GameSetup Modal - Pass the current club data to skip club selection step */}
        <GameSetup
          isOpen={showGameSetup}
          onClose={() => setShowGameSetup(false)}
          initialData={{
            club: course,
            course: course?.courses?.length === 1 ? course.courses[0] : null,
            name: `${course?.name || ''}`,
            date: new Date().toISOString(),
            holes: 18,
            startingHole: 1,
            format: 'strokeplay',
            players: [],
            contests: {
              longestDrive: false,
              closestToPin: false,
            },
            teeOptions: {
              default: 'white',
            },
            useHandicaps: true,
            isPrivate: false
          } as Partial<GameSetupType>}
          skipClubSelection={true}
        />
      </IonContent>
    </IonPage>
  );
};

export default Club;